/*
 * Waiver flow — pure model (canon §6 `waiver.flow.*`, FLEXIBLE).
 * Gate/block/receipt strings are the canon [chrome] templates VERBATIM;
 * consent state is per-SECTION only (canon §6 must-not: no per-clause ticks,
 * no single global "I agree"). All functions are pure and unit-tested.
 */
import type { WaiverDocument, WaiverTemplate } from '../../data/waiverTemplate'

/** Signing subject. Guardian ENTRY is not wired this cycle —
 *  // TODO(V1.1 kids amendment §12.2): guardian/child waiver entry from the
 *  // Junior Fun Run-style guardian card lands with the kids canon amendment.
 *  The chrome + doc-set structure below already keys off it (canon §6). */
export type WaiverSubject =
  | { kind: 'self' }
  | { kind: 'guardian'; who: string }

/** Per-section consent scratch key — `{docId}.{sectionIndex}`. Scratch is
 *  scoped per event and reset on submit/exit (canon §6 signed-record rule:
 *  "never carried between agreements"). */
export const sectionKey = (docId: string, index: number): string => `${docId}.${index}`

/**
 * Fill the signed-in participant's name into any `{{participant_name}}`
 * placeholders the resolver left for the CLIENT (BOARDROOM-2026-08-13-110303).
 * The org/event tokens ({{event_name}}/{{org_name}}/{{org_unit_name}}) are
 * resolved SERVER-SIDE by settings — the participant's identity is client-only,
 * so this one token is the FE's to fill so ZERO literal `{{...}}` reach the DOM.
 * Tolerant of internal whitespace + case. An empty name (no profile yet) falls
 * back to a neutral "the participant" rather than leaving a broken gap — never
 * fabricates a specific person's name.
 */
export function fillParticipant(text: string, participantName: string): string {
  const name = participantName.trim() || 'the participant'
  return text.replace(/\{\{\s*participant_name\s*\}\}/gi, name)
}

export type ConsentState = Record<string, boolean>

/** Count of REQUIRED sections in a doc not yet ticked. */
export function requiredLeft(doc: WaiverDocument, consents: ConsentState): number {
  return doc.sections.filter((s, i) => s.required && !consents[sectionKey(doc.id, i)]).length
}

/** A doc gates Continue on its required sections only — the optional section
 *  never blocks (canon §6 acceptance). */
export function docOk(doc: WaiverDocument, consents: ConsentState): boolean {
  return requiredLeft(doc, consents) === 0
}

/** Optional sections left unticked across ALL docs — the receipt's
 *  `Declined: {names}` list. */
export function declinedOptional(documents: WaiverDocument[], consents: ConsentState): string[] {
  const declined: string[] = []
  for (const d of documents) {
    d.sections.forEach((s, i) => {
      // Only sections that actually offer an OPTIONAL consent tick (organiser
      // authored consent text, not required) can be "declined". Read-only
      // sections of a typed-signature template have no tick, so they are never
      // declined — they're accepted by the signature.
      if (!s.required && s.consent && !consents[sectionKey(d.id, i)]) declined.push(s.name)
    })
  }
  return declined
}

/** Gate line above the sections [chrome]:
 *  `{N} section{s} still to accept` → `✓ All required sections accepted`. */
export function gateText(reqLeft: number): string {
  return reqLeft === 0
    ? '✓ All required sections accepted'
    : `${reqLeft} section${reqLeft === 1 ? '' : 's'} still to accept`
}

/** Doc block bar [chrome]: `Accept the {n} remaining required section{s} to continue`. */
export function docBlockText(reqLeft: number): string {
  return `Accept the ${reqLeft} remaining required section${reqLeft === 1 ? '' : 's'} to continue`
}

/**
 * Signature match — INDEPENDENT MUST-FIX (BOARDROOM-2026-08-09-133534): the
 * typed signature must equal the participant's PROFILE NAME case-insensitively.
 * BOTH sides are normalised (trim + locale-aware lowercase) before comparing —
 * the previous gate accepted any ≥2-char string; this one accepts ONLY the
 * profile name (canon §6 must-not: the input must not accept a non-matching name).
 */
export function normaliseSignature(name: string): string {
  return name.trim().toLocaleLowerCase()
}

export function signatureMatches(typed: string, expected: string): boolean {
  const t = normaliseSignature(typed)
  return t.length > 0 && t === normaliseSignature(expected)
}

/** Sign-screen hint [chrome]: `Must match {name} exactly` →
 *  `✓ Signature matches your profile name` / `Doesn't match {name}`. */
export function signHint(typed: string, expected: string): { text: string; tone: 'idle' | 'ok' | 'bad' } {
  if (!typed.trim()) return { text: `Must match ${expected} exactly`, tone: 'idle' }
  return signatureMatches(typed, expected)
    ? { text: '✓ Signature matches your profile name', tone: 'ok' }
    : { text: `Doesn't match ${expected}`, tone: 'bad' }
}

/** Sign block bar [chrome]: `Type your full name to sign` /
 *  `Your signature must match {name} exactly`. */
export function signBlockText(typed: string, expected: string): string {
  return typed.trim() ? `Your signature must match ${expected} exactly` : 'Type your full name to sign'
}

/** Doc intro [chrome]. Read-only re-open swaps to the signed-record intro
 *  (canon §6 signed-record rule). */
export function docIntro(
  doc: WaiverDocument,
  subject: WaiverSubject,
  organiser: string,
  signed: boolean,
): string {
  const party = subject.kind === 'guardian' ? subject.who : 'you'
  if (signed) {
    return `Between ${party} and ${organiser}. This is the agreement as you accepted it — nothing here can be changed.`
  }
  return doc.intro ?? `Between ${party} and ${organiser}. Accept each section to continue — Trumin keeps the signed record.`
}

/** Sign-screen chrome strings [chrome] (guardian signs THEIR OWN name). */
export function signChrome(subject: WaiverSubject, who: string): {
  heading: string
  intro: string
  signedFor: string
  label: string
} {
  if (subject.kind === 'guardian') {
    return {
      heading: 'Sign as guardian',
      intro: `You are signing as ${who}'s parent or legal guardian.`,
      signedFor: `Signing for ${who} · Guardian`,
      label: 'Type your full name (the guardian)',
    }
  }
  return {
    heading: 'Sign to finish',
    intro: 'Your typed name is your legal signature.',
    signedFor: `Signing as ${who}`,
    label: 'Type your full name',
  }
}

/** `{title} {version}` list, `·`-joined — receipt `Documents` row + the
 *  templateVersion carried to my-api. */
export function docsLine(docs: Array<{ title: string; version: string }>): string {
  return docs.map((d) => `${d.title} ${d.version}`).join(' · ')
}

/** Display stamp, transcribed from the DC: `{d} {Mon} {yyyy}, {HH}:{MM}`. */
export function formatStamp(now: Date): string {
  const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getDate()} ${MON[now.getMonth()]} ${now.getFullYear()}, ${pad(now.getHours())}:${pad(now.getMinutes())}`
}

/** Receipt rows [chrome] — keys are the canon-verbatim `data-k` values. */
export interface ReceiptRow { k: string; v: string }
export function receiptRows(record: {
  guardian: boolean
  participant: string
  signedBy: string
  when: string
  docs: Array<{ title: string; version: string }>
  declined: string[]
}): ReceiptRow[] {
  return [
    { k: 'Documents', v: docsLine(record.docs) },
    { k: 'Participant', v: record.participant },
    { k: record.guardian ? 'Signed by (guardian)' : 'Signed by', v: record.signedBy },
    { k: 'Signed at', v: record.when },
    {
      k: 'Optional consents',
      v: record.declined.length ? `Declined: ${record.declined.join(', ')}` : 'All accepted',
    },
  ]
}

/**
 * `Signed on {date} at {time}` — the unmistakable signature line shown at the
 * foot of a read-only re-opened agreement and on the printable receipt
 * (CMT-2026-08-13-121331). Derived from the stored display stamp
 * (`{d} {Mon} {yyyy}, {HH}:{MM}` — see formatStamp); falls back gracefully if a
 * record's stamp is not in the `date, time` shape. Never fabricates a time.
 */
export function signedOnLine(record: { when: string }): string {
  const idx = record.when.lastIndexOf(', ')
  if (idx < 0) return `Signed on ${record.when}`
  const date = record.when.slice(0, idx)
  const time = record.when.slice(idx + 2)
  return `Signed on ${date} at ${time}`
}

/** Minimal HTML escape for values interpolated into the print document. */
function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Build a self-contained, printable HTML document for the signed waiver — the
 * "Download the signed PDF" action prints THIS to PDF (no network, no external
 * library: the offline/CSP posture forbids CDN fetches). Pure + unit-tested; the
 * impure window/print step lives in the component.
 *
 * CMT-2026-08-14-032 (CRITICAL): the download used to be a bare RECEIPT STUB —
 * just the doc titles + signature evidence, none of the actual legal text the
 * signer agreed to, and no branding. It is now a proper branded legal document:
 *   (1) BRANDING — organiser wordmark + event, matched to the branded look. The
 *       logo is a wordmark (not a remote <img>) on purpose: the print window is
 *       about:blank and the CSP/offline posture forbids a CDN/remote fetch; an
 *       actual raster logo would have to arrive as an embedded data-URI (settings
 *       provides the asset) — a follow-up, tracked in the completion.
 *   (2) LEGAL BODY — the full acknowledged document tree (documents → sections →
 *       clauses WITH bodies + guardian declaration), rendered with {{participant}}
 *       filled exactly as shown on screen. This is the EXACT text the signer read
 *       and accepted (the on-screen template IS settings' resolved template via
 *       the waiverTemplate seam), so nothing is fabricated
 *       (PROGRAMME-AD-NO-FABRICATED-DATA-IN-BUILDS) and no extra network hop is
 *       needed in the print path. Each section carries its ACCEPTED / DECLINED
 *       state from the stored record.
 *   (3) RECEIPT + SIGNATURE — retained verbatim (participant, signed-by, stamp,
 *       optional consents, script-font signature).
 * `documents`/`who` are optional so an older caller (or a record with no template
 * on hand) still renders the receipt-only form rather than crashing.
 */
export function buildReceiptPrintHtml(
  record: {
    guardian: boolean
    participant: string
    signedBy: string
    when: string
    docs: Array<{ title: string; version: string }>
    declined: string[]
  },
  opts: {
    event?: string
    organiser?: string
    documents?: WaiverDocument[]
    who?: string
    /** CMT-2026-08-15-011: the EVENT BANNER (OnePulse) shown across the TOP,
     *  replacing the Trumin wordmark (wrong mark for an event doc). A central-R2 /
     *  org_units brand-pack URL; the print window loads it remotely (fresh
     *  about:blank window, not under the app CSP). Absent → graceful fallback to
     *  the organiser wordmark text (never a broken image). */
    eventBannerUrl?: string
    /** The small Trumin LOGO fixed BOTTOM-RIGHT on every printed page (central-R2
     *  wordmark). Absent → omitted. */
    truminLogoUrl?: string
  } = {},
): string {
  const who = opts.who || record.participant
  const rows = receiptRows(record)
    .map(
      (r) =>
        `<tr><th scope="row">${esc(r.k)}</th><td>${esc(r.v)}</td></tr>`,
    )
    .join('')
  const header = [opts.organiser, opts.event]
    .filter((v): v is string => !!v)
    .map(esc)
    .join(' · ')

  // (2) Full acknowledged legal body — the actual agreement, not just titles.
  const legal = (opts.documents ?? [])
    .map((doc) => {
      const sections = doc.sections
        .map((s) => {
          const accepted = recordTick(s, record.declined)
          const state = s.required
            ? `<span class="state ok">Accepted — required</span>`
            : accepted
              ? `<span class="state ok">Accepted</span>`
              : `<span class="state no">Declined — optional</span>`
          const clauses = s.clauses
            .map(
              (c) =>
                `<div class="cl${c.sub ? ' sub' : ''}"><span class="n">${esc(c.num)}</span>`
                + `<span class="t">${esc(fillParticipant(c.text, who))}</span></div>`,
            )
            .join('')
          const note = s.note ? `<p class="note">${esc(fillParticipant(s.note, who))}</p>` : ''
          return `<section class="sec"><h3>${esc(fillParticipant(s.name, who))} ${state}</h3>${clauses}${note}</section>`
        })
        .join('')
      return `<article class="doc"><p class="eyebrow">${esc(doc.version)}</p>`
        + `<h2>${esc(fillParticipant(doc.title, who))}</h2>${sections}</article>`
    })
    .join('')

  const title = legal ? 'Signed waiver' : 'Signed waiver — receipt'
  return `<!doctype html><html lang="en"><head><meta charset="utf-8">`
    + `<meta name="viewport" content="width=device-width, initial-scale=1">`
    + `<title>${esc(title)}</title><style>`
    + `*{box-sizing:border-box}`
    + `body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;`
    + `color:#111;margin:0;padding:32px;line-height:1.5}`
    + `.wrap{max-width:640px;margin:0 auto}`
    + `.eyebrow{font-size:11px;letter-spacing:.1em;text-transform:uppercase;color:#666;margin:0 0 6px}`
    // (1) branding band — organiser wordmark + event + accent rule
    + `.brand{border-bottom:3px solid #111;padding-bottom:12px;margin-bottom:20px}`
    + `.brand .mark{font-size:20px;font-weight:800;letter-spacing:-.01em;margin:0}`
    + `.brand .ev{font-size:13px;color:#555;margin:2px 0 0}`
    // CMT-011: event banner across the top + Trumin logo fixed bottom-right on
    // EVERY printed page (position:fixed repeats per page in print).
    + `.brand .banner{display:block;max-width:100%;height:auto;margin:0 0 4px;border-radius:6px}`
    + `.pagelogo{position:fixed;right:14px;bottom:12px;width:66px;height:auto;opacity:.9}`
    + `h1{font-size:22px;margin:20px 0 4px}h2{font-size:17px;margin:22px 0 2px}`
    + `h3{font-size:13.5px;margin:14px 0 6px}`
    + `.state{font-size:10px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;`
    + `padding:2px 6px;border-radius:4px;vertical-align:middle;margin-left:6px}`
    + `.state.ok{background:#e7f6ec;color:#1a7f37}.state.no{background:#f3f4f6;color:#888}`
    + `.cl{display:flex;gap:8px;font-size:12.5px;margin:0 0 5px}`
    + `.cl.sub{margin-left:18px}.cl .n{color:#888;flex:none;min-width:34px}.cl .t{flex:1}`
    + `.note{font-size:11.5px;color:#666;margin:4px 0 0}`
    + `.doc+.doc{margin-top:8px;padding-top:8px;border-top:1px solid #eee}`
    + `.stamp{font-size:13px;color:#333;font-weight:700;margin:0 0 20px}`
    + `.receipt{margin-top:26px;padding-top:14px;border-top:2px solid #111}`
    + `table{width:100%;border-collapse:collapse;font-size:13px}`
    + `th,td{text-align:left;vertical-align:top;padding:9px 0;border-bottom:1px solid #e2e2e2}`
    + `th{color:#666;font-weight:400;width:40%}td{font-weight:700;text-align:right}`
    + `.sig{margin-top:24px;padding-top:16px;border-top:1px solid #e2e2e2}`
    + `.sig .name{font-family:Georgia,'Times New Roman',serif;font-style:italic;font-size:20px}`
    + `.foot{margin-top:20px;font-size:11px;color:#888}`
    + `@media print{body{padding:0}}`
    + `</style></head><body><div class="wrap">`
    // Trumin logo fixed bottom-right on every page (CMT-011). Omitted if no URL.
    + (opts.truminLogoUrl ? `<img class="pagelogo" src="${esc(opts.truminLogoUrl)}" alt="Trumin">` : '')
    // (1) branding header — CMT-011: the EVENT BANNER across the top (replacing
    // the Trumin wordmark, which is wrong for an event document). Falls back to
    // the organiser wordmark text when no banner URL is wired (never broken img).
    + `<div class="brand">`
    + (opts.eventBannerUrl
        ? `<img class="banner" src="${esc(opts.eventBannerUrl)}" alt="${esc(opts.event || 'Event')}">`
        : `<p class="mark">${esc(opts.organiser || 'Trumin')}</p>`)
    + (opts.event ? `<p class="ev">${esc(opts.event)}</p>` : '')
    + `</div>`
    // (2) the agreed legal document
    + legal
    // (3) receipt + signature evidence
    + `<div class="receipt">`
    + (header ? `<p class="eyebrow">${header}</p>` : '')
    + `<h1>Waiver signed</h1>`
    + `<p class="stamp">${esc(signedOnLine(record))} · timestamp and device recorded</p>`
    + `<table><tbody>${rows}</tbody></table>`
    + `<div class="sig"><div class="eyebrow">Signature</div>`
    + `<div class="name">${esc(record.signedBy)}</div></div>`
    + `<p class="foot">This is your signed record, kept in your Trumin account.</p>`
    + `</div>`
    + `</div></body></html>`
}

/** Read-only tick derivation (canon §6: "ticks derived from the record") —
 *  required sections were necessarily accepted; optional ones accepted unless
 *  in the record's declined list. */
export function recordTick(
  section: { name: string; required: boolean },
  declined: string[],
): boolean {
  return section.required || !declined.includes(section.name)
}

/** Step counter [chrome]: `Step {n} of {N}`; the receipt (and any read-only
 *  re-open) reads `SIGNED`. N = docs + the sign step. */
export function stepCounter(stepIndex: number, docCount: number, signed: boolean): string {
  return signed ? 'SIGNED' : `Step ${stepIndex + 1} of ${docCount + 1}`
}

/** Named-party line [chrome]: `{org} · {event}`. */
export function namedParty(template: WaiverTemplate): string {
  return `${template.organiser} · ${template.event}`
}
